import { Navigate, useRoutes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import BaseLayout from "../layouts/BaseLayout";
import AuthLayout from "../layouts/AuthLayout";
import SettingTemplate from "../pages/SettingTemplate/SettingTemplate";
import AllTransactions from "../pages/AllTransactions/AllTransactions";
import BatchTransaction from "../pages/BatchTransaction/BatchTransaction";
import Home from "../pages/Home/Home";
import ViewCredential from "../pages/ViewCredential/ViewCredential";
import AuthContext from "../store/auth-context";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ActivateAccount from "../pages/ActivateAccount/ActivateAccount";
import TransactionReport from "../pages/TransactionReport/TransactionReport";
import Profile from "../pages/Profile/Profile";
import ForgotPasswordAccount from "../pages/ForgotPasswordAccount/ForgotPasswordAccount";
import LegacyValidate from "../pages/LegacyValidate/LegacyValidate";
import LegacyTemplateDetails from "../pages/LegacyTemplateDetails/LegacyTemplateDetails";
import ViewLegacyCredential from "../pages/ViewLegacyCredential/ViewLegacyCredential";
import VpResult from "../pages/VpResult/VpResult";
import VpQrCode from "../pages/VpQrCode/VpQrCode";
import VpVerificationCategory from "../pages/VpVerificationCategory/VpVerificationCategory";
import VpVerificationCheck from "../pages/VpVerificationCheck/VpVerificationCheck";
import CombinedValidate from "../pages/CombinedValidate/CombinedValidate";
import FullScreenLoader from "../common/FullScreenLoader/FullScreenLoader";
import Details from "../pages/Details/Details";
import LegacyUserDetails from "../pages/LegacyUserDetails/LegacyUserDetails";
import PaymentDetails from "../pages/PaymentDetails/PaymentDetails";
import Summary from "../pages/Summary/Summary";
import OcrForm from "../pages/Ocr/OcrForm";
import OcrProcessing from "../pages/Ocr/OcrProcessing";
import ProtectedRoute from "./ProtectedRoute";
import { appConfig } from "../configs/tenantConfig";
import {
  isPublicVerificationPortalEnabled,
} from "../configs/featureFlags";
import PublicVerificationPortal from "../pages/PublicVerificationPortal/PublicVerificationPortal";
import PublicVerificationResultPage from "../pages/PublicVerificationPortal/PublicVerificationResultPage";

const getResolvedLanguage = (i18n) =>
  String(i18n.resolvedLanguage || i18n.language || appConfig.defaultLanguage || "")
    .split("-")[0];

const RouteWrapper = ({
  element,
  title,
  useLocalizedDocumentTitle = false,
}) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const language = getResolvedLanguage(i18n);
    const localizedTitle =
      useLocalizedDocumentTitle &&
      (appConfig.documentTitles?.[language] ||
        appConfig.documentTitles?.[appConfig.defaultLanguage]);

    if (localizedTitle) {
      document.title = localizedTitle;
      return;
    }

    if (title) {
      document.title = `${title} | ${appConfig.pageTitle}`;
    }
  }, [title, useLocalizedDocumentTitle, i18n.language, i18n.resolvedLanguage]);

  return element;
};

const Router = () => {
  const authContext = useContext(AuthContext);
  const publicVerificationPortalEnabled = isPublicVerificationPortalEnabled();
  const useInstrumentedRoutes = useMemo(() => {
    return process.env.REACT_APP_SENTRY_DSN &&
      process.env.NODE_ENV === "production"
      ? Sentry.wrapUseRoutes(useRoutes)
      : useRoutes;
  }, []);

  const homeRoute = { path: "", element: <Home /> };
  const publicRoutes = [
    {
      path: "users/activate/:uuid/:token",
      element: <ActivateAccount />,
      title: "Activate Account",
    },
    {
      path: "users/update-password/:uuid/:token",
      element: authContext.isLoggedIn ? <Home /> : <ForgotPasswordAccount />,
      title: authContext.isLoggedIn ? "Home" : "Forgot Password",
    },
  ];

  const auditRoutes = [
    { path: "settings", element: <SettingTemplate />, title: "Setting" },
    { path: "profile", element: <Profile />, title: "Profile" },
    {
      path: "transaction/all",
      element: <AllTransactions />,
      title: "Transaction",
    },
    {
      path: "transaction/:transaction_id/:transaction_name",
      element: <BatchTransaction />,
      title: "Transactions",
    },

    { path: "validate", element: <CombinedValidate /> },
    { path: "legacy", element: <LegacyValidate /> },
    // { path: "vp/:category/:check_id/qrcode", element: <VpQrCode /> },
    // { path: "vp/:vp_instance_id/result", element: <VpResult /> },
    {
      path: "vp/category",
      element: <VpVerificationCategory />,
      title: "Request VP Category",
    },
    {
      path: "vp/check/:category_id/:category_name",
      element: <VpVerificationCheck />,
      title: "Request VP Check",
    },
    {
      path: "vp/qrcode/:category_name/:check_id",
      element: <VpQrCode />,
      title: "VP Qr Code",
    },
    {
      path: "vp/result/:vp_instance_id",
      element: <VpResult />,
      title: "VP Result",
    },
    {
      path: "validate/:credential_id",
      element: <ViewCredential />,
      title: "View Credential",
    },
    {
      path: "validate/legacy/:certificate_id",
      element: <ViewLegacyCredential />,
      title: "View Legacy Credential",
    },
    {
      path: "validate/transaction/report/:transaction_id",
      element: <TransactionReport />,
      title: "Transaction Report",
    },
    // Legacy Routes
    {
      path: "legacy/certificate-details",
      element: <LegacyTemplateDetails />,
      title: "Legacy Certificate Details",
    },
    {
      path: "legacy/user-details",
      element: <LegacyUserDetails />,
      title: "Legacy User Details",
    },
    {
      path: "legacy/payment-details/:transaction_id",
      element: <PaymentDetails />,
      title: "Legacy Payment Details",
    },
    {
      path: "legacy/summary",
      element: <Summary />,
      title: "Legacy Summary",
    },
    {
      path: "vc/payment-details/:transaction_id/:order_id",
      element: <PaymentDetails />,
      title: "VC Payment Details",
    },
    {
      path: "vc/summary",
      element: <Summary />,
      title: "VC Summary",
    },
    {
      path: "ocr/summary",
      element: <Summary />,
      title: "OCR Summary",
    },
    {
      path: "validate/transaction/:transaction_id",
      element: <Details />,
      title: "Details Page",
    },
    {
      path: "ocr/processing",
      element: <OcrProcessing />,
      title: "OCR Processing",
    },
    {
      path: "ocr/form",
      element: <OcrForm />,
      title: "OCR Form",
    },
    {
      path: "ocr/payment-details/:transaction_id/:order_id",
      element: <PaymentDetails />,
      title: "OCR Payment Details",
    },
    { element: <Navigate to="/verifier/validate" />, index: true },
  ];

  const routes = useInstrumentedRoutes([
    ...(publicVerificationPortalEnabled
      ? [
          {
            path: "/",
            element: (
              <RouteWrapper
                element={<PublicVerificationPortal />}
                title="Verify Digital Credentials"
                useLocalizedDocumentTitle
              />
            ),
          },
          {
            path: "/verification-result",
            element: (
              <RouteWrapper
                element={<PublicVerificationResultPage />}
                title="Verification Result"
                useLocalizedDocumentTitle
              />
            ),
          },
        ]
      : []),
    {
      path: "/verifier",
      element: <AuthLayout />,
      children: auditRoutes.map((route) => ({
        ...route,
        element: (
          <ProtectedRoute>
            <RouteWrapper element={route.element} title={route.title} />
          </ProtectedRoute>
        ),
      })),
    },
    {
      path: "/",
      element: <BaseLayout />,
      children: [
        ...(publicVerificationPortalEnabled ? [] : [homeRoute]),
        ...publicRoutes,
      ].map((route) => ({
        ...route,
        element: <RouteWrapper element={route.element} title={route.title} />,
      })),
    },
    {
      path: "*",
      element: authContext.isLoggedIn ? (
        <Navigate to="/verifier/validate" />
      ) : (
        <Navigate to="/" />
      ),
    },
  ]);

  if (authContext.isLoading) {
    return <FullScreenLoader />;
  }

  return routes;
};

export default Router;
